import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, Save, Calendar } from "lucide-react";
import { format } from "date-fns";

interface PrenatalIntake {
  id: string;
  first_name: string;
  last_name: string;
  medicaid_id: string | null;
}

interface TimeLogDialogProps {
  open: boolean;
  onClose: () => void;
  intake: PrenatalIntake;
}

interface TimeEntry {
  date: string;
  description_code: string;
  place_of_service: string;
  time_spent: string;
  units: number;
  documentation: string;
}

const DESCRIPTION_CODES = [
  { code: "MD", label: "Member Contact – Direct, In-Person" },
  { code: "CD", label: "Collateral Contact – Direct, In-Person" },
  { code: "MT", label: "Member Contact – Telephone/Audio-Only" },
  { code: "CT", label: "Collateral Contact – Telephone/Audio-Only" },
  { code: "MV", label: "Member Contact – Virtual/Audio-Visual" },
  { code: "CV", label: "Collateral Contact – Virtual/Audio-Visual" },
  { code: "S", label: "Staffing / Consultations" },
  { code: "R", label: "Recordkeeping" },
];

const PLACE_OF_SERVICE_OPTIONS = [
  "Member Home",
  "Office",
  "Community Location",
  "Clinic",
  "Virtual/Telehealth",
  "Other",
];

export function TimeLogDialog({ open, onClose, intake }: TimeLogDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedMonth, setSelectedMonth] = useState(() => format(new Date(), "yyyy-MM"));
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [providerName, setProviderName] = useState("");
  const [providerTitle, setProviderTitle] = useState("");
  const [supervisorName, setSupervisorName] = useState("");
  const [supervisorTitle, setSupervisorTitle] = useState("Registered Nurse");

  const { data: existingLog, isLoading } = useQuery({
    queryKey: ["pncc-time-log", intake.id, selectedMonth],
    queryFn: async () => {
      const logMonth = `${selectedMonth}-01`;
      const { data, error } = await supabase
        .from("pncc_time_logs")
        .select("*")
        .eq("prenatal_intake_id", intake.id)
        .eq("log_month", logMonth)
        .maybeSingle();

      if (error) throw error;
      
      if (data && data.time_entries) {
        setEntries(data.time_entries as unknown as TimeEntry[]);
        setProviderName(data.provider_name || "");
        setProviderTitle(data.provider_title || "");
        setSupervisorName(data.supervising_provider_name || "");
        setSupervisorTitle(data.supervising_provider_title || "");
      } else {
        setEntries([]);
      }
      
      return data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");

      const totalUnits = entries.reduce((sum, entry) => sum + (entry.units || 0), 0);
      const logMonth = `${selectedMonth}-01`;

      const logData = {
        prenatal_intake_id: intake.id,
        member_id: intake.id,
        member_name: `${intake.first_name} ${intake.last_name}`,
        member_medicaid_id: intake.medicaid_id,
        provider_name: providerName,
        provider_title: providerTitle,
        provider_agency: "Empowered Families Network",
        supervising_provider_name: supervisorName,
        supervising_provider_title: supervisorTitle,
        log_month: logMonth,
        time_entries: JSON.parse(JSON.stringify(entries)),
        total_units: totalUnits,
        status: "draft",
      };

      if (existingLog) {
        const { error } = await supabase
          .from("pncc_time_logs")
          .update(logData)
          .eq("id", existingLog.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("pncc_time_logs")
          .insert(logData);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success("Time log saved successfully");
      queryClient.invalidateQueries({ queryKey: ["pncc-time-log", intake.id, selectedMonth] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to save time log");
    },
  });

  const addEntry = () => {
    setEntries([
      ...entries,
      {
        date: format(new Date(), "yyyy-MM-dd"),
        description_code: "MD",
        place_of_service: "Member Home",
        time_spent: "1 hour",
        units: 4.0,
        documentation: "",
      },
    ]);
  };

  const removeEntry = (index: number) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const updateEntry = (index: number, field: keyof TimeEntry, value: any) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], [field]: value };
    setEntries(updated);
  };

  const totalUnits = entries.reduce((sum, entry) => sum + (entry.units || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            PNCC Monthly Time Log
          </DialogTitle>
          <DialogDescription>
            {intake.first_name} {intake.last_name} • Medicaid ID: {intake.medicaid_id || "N/A"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Month Selector & Provider Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Log Month</Label>
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Provider Name & Title</Label>
              <Input
                value={providerName}
                onChange={(e) => setProviderName(e.target.value)}
                placeholder="e.g., Jane Doe, CHW"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Supervising Provider</Label>
              <Input
                value={supervisorName}
                onChange={(e) => setSupervisorName(e.target.value)}
                placeholder="e.g., Dr. Smith"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Supervisor Title</Label>
              <Input
                value={supervisorTitle}
                onChange={(e) => setSupervisorTitle(e.target.value)}
                placeholder="e.g., Registered Nurse"
                className="mt-1"
              />
            </div>
          </div>

          {/* Description Codes Legend */}
          <div className="bg-muted p-3 rounded-lg">
            <p className="text-sm font-medium mb-2">Description Codes:</p>
            <div className="flex flex-wrap gap-2 text-xs">
              {DESCRIPTION_CODES.map((code) => (
                <Badge key={code.code} variant="outline">
                  {code.code} = {code.label}
                </Badge>
              ))}
            </div>
          </div>

          {/* Time Entries Table */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Time Entries</h4>
              <div className="flex items-center gap-4">
                <Badge variant="secondary" className="text-lg px-3 py-1">
                  Total: {totalUnits.toFixed(1)} Units
                </Badge>
                <Button variant="outline" size="sm" onClick={addEntry}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Entry
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border rounded-lg">
                No entries for this month. Click "Add Entry" to start logging time.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">Date</TableHead>
                    <TableHead className="w-24">Code</TableHead>
                    <TableHead className="w-32">Place</TableHead>
                    <TableHead className="w-24">Time</TableHead>
                    <TableHead className="w-20">Units</TableHead>
                    <TableHead>Documentation</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Input
                          type="date"
                          value={entry.date}
                          onChange={(e) => updateEntry(index, "date", e.target.value)}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={entry.description_code}
                          onValueChange={(value) => updateEntry(index, "description_code", value)}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DESCRIPTION_CODES.map((code) => (
                              <SelectItem key={code.code} value={code.code}>
                                {code.code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={entry.place_of_service}
                          onValueChange={(value) => updateEntry(index, "place_of_service", value)}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PLACE_OF_SERVICE_OPTIONS.map((place) => (
                              <SelectItem key={place} value={place}>
                                {place}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={entry.time_spent}
                          onChange={(e) => updateEntry(index, "time_spent", e.target.value)}
                          placeholder="e.g., 1 hour"
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.5"
                          value={entry.units}
                          onChange={(e) => updateEntry(index, "units", parseFloat(e.target.value) || 0)}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Textarea
                          value={entry.documentation}
                          onChange={(e) => updateEntry(index, "documentation", e.target.value)}
                          placeholder="Document activities..."
                          className="min-h-[60px] text-xs"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEntry(index)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Time Log
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
